import React from 'react';
import ReactDOM from 'react-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import useHoverEffect from 'components/hooks/useHoverEffects';
import classnames from 'classnames';
import { ReactComponent as TaskIcon } from 'icons/task.svg';
import { ReactComponent as TaskReviewedIcon } from 'icons/task-reviewed.svg';
import { isNullOrUndefined } from 'utils/object-utils';
import { getElementBoundaryInfo } from 'utils/ocr-element-utils';
import BookmarkDetailsOCRTooltipInfo from './bookmark-details-ocr-tooltip-info';
import {
  STATEMENT_BOOKMARK_ID_PREFIX,
  STATEMENT_LEFT_BOOKMARK_ID_PREFIX,
} from 'constants/feature/statement-content-constants';

export const SECTION_ANCHOR_BLOCK = 'section-anchor';
export const SECTION_ANCHOR_BLOCK_OCR = 'section-anchor-ocr';
export const LEFT_SECTION_ANCHOR_BLOCK_OCR = 'left-section-anchor-ocr';
export const SECTION_ANCHOR_BLOCK_ID = 'section-anchor-id';
export const REVIEW_ICON_SIZE = 16;
const MAX_POLYGON_WIDTH = 5;
const PADDING = 6;
const BORDER_THICKNESS = '1px';

export const getInitials = (string) => {
  if (isNullOrUndefined(string)) return;
  if (string.includes('@')) {
    return string.toLowerCase();
  }
  const initials = string.replace(/[^a-zA-Z- ]/g, '').match(/\b\w/g);
  return initials[0] + initials[1];
};

const BookmarkDetailsOCR = ({
  polygonInPixel,
  showIcon,
  handleElementClick,
  sectionId,
  color,
  bookmarkId,
  sectionReview,
  sectionAssignments,
  displayElementDetailsOnStatementPage,
  selectedSection,
  projectUsersList,
  isOCR = true,
  shouldSectionAnchorDisabled,
  leftSideView,
  isSideBySideView,
}) => {
  const renderIcon = (shouldRenderIcon) => {
    if (!shouldRenderIcon) {
      return null;
    }
    const hasBeenReviewed = sectionReview.hasSectionBeenReviewed(
      bookmarkId,
      isOCR,
    );
    if (hasBeenReviewed) {
      return (
        <TaskReviewedIcon
          name={'anchor'}
          width={REVIEW_ICON_SIZE}
          height={REVIEW_ICON_SIZE}
        />
      );
    }
    return (
      <TaskIcon
        name={'anchor'}
        width={REVIEW_ICON_SIZE}
        height={REVIEW_ICON_SIZE}
      />
    );
  };

  const shouldAssignmentInitialsBeRendered = (userId, bookmarkId) => {
    if (bookmarkId in sectionReview.sectionReviewsMap) {
      return !(userId in sectionReview.sectionReviewsMap[bookmarkId]);
    }
    return true;
  };

  const { polygonWidth, polygonHeight, polygonLeft, polygonTop } =
    getElementBoundaryInfo(polygonInPixel);

  const updatedPolygonWidth =
    polygonWidth > MAX_POLYGON_WIDTH ? polygonWidth : polygonWidth + PADDING;
  const updatedPolygonHeight =
    polygonHeight > MAX_POLYGON_WIDTH ? polygonHeight : polygonHeight + PADDING;

  let remainingSpaceToAddOrSubtract =
    updatedPolygonHeight - REVIEW_ICON_SIZE - 2;

  const styles = {
    position: 'absolute',
    zIndex: 50,
    top: polygonTop,
    left: polygonLeft,
    width: updatedPolygonWidth,
    height: updatedPolygonHeight,
    borderBottom: `${BORDER_THICKNESS} solid #000000`,
    cursor: 'pointer',
  };

  const BOOKMARK_BLOCK =
    isSideBySideView && leftSideView
      ? `${LEFT_SECTION_ANCHOR_BLOCK_OCR}`
      : `${SECTION_ANCHOR_BLOCK_OCR}`;

  const { handleMouseEnter, handleMouseLeave } = useHoverEffect(
    `${BOOKMARK_BLOCK}`,
    bookmarkId,
  );

  const BOOKMARK_BLOCK_ID_PREFIX =
    isSideBySideView && leftSideView
      ? STATEMENT_LEFT_BOOKMARK_ID_PREFIX
      : STATEMENT_BOOKMARK_ID_PREFIX;

  const shouldShowTooltip =
    (Array.isArray(sectionReview.sectionReviews) &&
      sectionReview.sectionReviews.filter(
        (review) => review['bookmarkId'] === bookmarkId,
      ).length > 0) ||
    sectionAssignments.sectionAssignments.filter(
      (assignments) =>
        assignments.bookmarkId === bookmarkId &&
        shouldAssignmentInitialsBeRendered(
          assignments.userId,
          assignments.bookmarkId,
        ),
    ).length > 0;

  return (
    <>
      {displayElementDetailsOnStatementPage && (
        <div
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            zIndex: 50,
            top: polygonTop + remainingSpaceToAddOrSubtract,
            left: polygonInPixel[0] - REVIEW_ICON_SIZE,
            cursor: 'pointer',
          }}
          onClick={() => {
            handleElementClick(sectionId, bookmarkId);
          }}
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
          className={classnames(
            {
              [`${BOOKMARK_BLOCK}__${color}`]:
                selectedSection && selectedSection.bookmarkId === bookmarkId,
              [`${BOOKMARK_BLOCK}--disabled`]: shouldSectionAnchorDisabled,
            },
            `${BOOKMARK_BLOCK}-${bookmarkId}`,
          )}
          id={`${BOOKMARK_BLOCK_ID_PREFIX}${bookmarkId}`}
        >
          {renderIcon(showIcon)}
        </div>
      )}

      <div
        style={styles}
        onClick={() => {
          handleElementClick(sectionId, bookmarkId);
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        className={classnames(
          {
            [`${BOOKMARK_BLOCK}__${color}`]:
              selectedSection && selectedSection.bookmarkId === bookmarkId,
            [`${BOOKMARK_BLOCK}--disabled`]: shouldSectionAnchorDisabled,
          },
          `${BOOKMARK_BLOCK}-${bookmarkId}`,
          `${BOOKMARK_BLOCK}--container`,
        )}
        onMouseEnter={() => handleMouseEnter()}
        onMouseLeave={() => handleMouseLeave()}
        id={`${BOOKMARK_BLOCK_ID_PREFIX}${bookmarkId}`}
      ></div>
      {shouldShowTooltip &&
        ReactDOM.createPortal(
          <ReactTooltip
            className={'chatbot-tooltip'}
            anchorSelect={`#${BOOKMARK_BLOCK_ID_PREFIX}${bookmarkId}`}
            place="right"
          >
            {
              <BookmarkDetailsOCRTooltipInfo
                sectionAssignments={sectionAssignments}
                shouldAssignmentInitialsBeRendered={
                  shouldAssignmentInitialsBeRendered
                }
                bookmarkId={bookmarkId}
                projectUsersList={projectUsersList}
                sectionReview={sectionReview}
              />
            }
          </ReactTooltip>,
          document.getElementById('tooltip-root'),
        )}
    </>
  );
};

export default BookmarkDetailsOCR;
